import api, { setApiUserToken } from 'config/api'
import {
  ICancelTransactionDto,
  IFormattedUser,
  IKycStatusResponse,
  ISendVipDto,
  ITransaction,
  IUserDevice,
  IUserReferrals,
  IUserSummary,
} from 'store/fetchers/users/types'
import { getStorageItem, STORAGE_KEYS } from 'store/reducers/storage'

export const getUser = async (): Promise<IFormattedUser> => {
  try {
    const { data } = await api.get('/v2/users')
    return data.response
  } catch (error) {
    throw new Error(error)
  }
}

export const getUserReferrals = async (): Promise<IUserReferrals> => {
  try {
    const { data } = await api.get('/v2/users/referral')
    return data.response
  } catch (err) {
    return null
  }
}

export const requestUserReferral = async (): Promise<unknown> => {
  try {
    const { data } = await api.post('/v2/users/referral-request')
    return data.response
  } catch (err) {
    throw new Error(err)
  }
}

export const sendVip = async (params: ISendVipDto): Promise<unknown> => {
  try {
    const { data } = await api.post('/v2/users/send-vip', { params: params })
    return data.result
  } catch (err) {
    return err?.response?.message ?? ''
  }
}

export const updateUser = async (params: {
  code_2fa: string
  verification_token: [string, string]
}): Promise<boolean> => {
  try {
    const { data } = await api.put('/v2/users/credentials', {
      params: params,
    })
    return data.result
  } catch (error) {
    return false
  }
}

export const setFallbackEmail = async (params: {
  code_2fa: string
  verification_token: string[]
  new_email: string
}) => {
  try {
    const { data } = await api.put('/v2/users/fallback-email', {
      params: params,
    })
    return {
      res: data.result
    }
  } catch (error) {
    const msg = error?.response?.message || error?.response?.error || ''

    return {
      error: msg
    }
  }
}

export const changeEmail = async (params: {
  verification_token: [string, string]
  new_email: string
  code_2fa: string
}): Promise<boolean> => {
  try {
    const { data } = await api.put('/v2/users/change-email', {
      params: params,
    })
    return data.result
  } catch (error) {
    return false
  }
}

export const changePhone = async (params: {
  code_2fa: string
  verification_token: string
  new_phone: string
}): Promise<boolean> => {
  try {
    const { data } = await api.put('/v2/users/change-phone', {
      params: params,
    })
    return data.result
  } catch (error) {
    return false
  }
}

export const fetchTransactions = async (params: {
  accountType?: string
  accountId?: string
  transactionType?: string
  currencyCode?: string
  currencyNetwork?: string
  search?: string
}): Promise<ITransaction[]> => {
  try {
    const { data } = await api.get('/v2/users/transactions', {
      params,
    })
    return data.response
  } catch (err) {
    return []
  }
}

export const getDevices = async (): Promise<IUserDevice[]> => {
  try {
    const { data } = await api.get('/v2/users/saved-device')
    return data.response
  } catch (err) {
    return []
  }
}

export const changeTwitterUsername = async (params: { twitter_username: string }): Promise<boolean> => {
  try {
    const { data } = await api.put('/v2/users/change-twitter-username', {
      params: params,
    })
    return data.result
  } catch (error) {
    return false
  }
}

export const getUserKycStatus = async (): Promise<IKycStatusResponse> => {
  try {
    const token = getStorageItem(STORAGE_KEYS.AUTH_TOKEN)
    await setApiUserToken(token)
    const { data } = await api.get('/v2/users/kyc-status')
    return data.response
  } catch (error) {
    return null
  }
}

export const cancelTransaction = async (params: ICancelTransactionDto): Promise<boolean> => {
  try {
    const { data } = await api.post('/v2/users/cancel-transaction', { params: params })
    return data.result
  } catch (err) {
    return false
  }
}

export const createApplicant = async (): Promise<string> => {
  try {
    const { data } = await api.post('/v2/users/kyc-applicant')
    return data.response
  } catch (err) {
    return null
  }
}

export const getKYCLink = async (): Promise<string> => {
  try {
    const { data } = await api.post('/v2/users/kyc-link')
    return data.response
  } catch (err) {
    return null
  }
}

export const getUserSummary = async (): Promise<IUserSummary> => {
  try {
    const { data } = await api.get('/v2/users/summary')
    return data?.response
  } catch (err) {
    return null
  }
}
